import React from "react";
import BookingFlowRadioIndicator from "./booking-flow-radio-indicator";
import styles from "./booking-flow-selectable-card.module.css";

type BookingFlowSelectableCardProps = {
    checked: boolean;
    onSelect: () => void;
    variant?: "light" | "dark";
    icon?: React.ReactNode;
    eyebrow?: React.ReactNode;
    headline: React.ReactNode;
    priceChip?: React.ReactNode;
    children?: React.ReactNode;
    className?: string;
};

export const BookingFlowSelectableCard: React.FunctionComponent<BookingFlowSelectableCardProps> = ({
    checked,
    onSelect,
    variant = "light",
    icon,
    eyebrow,
    headline,
    priceChip,
    children,
    className,
}) => {
    const isDark = variant === "dark";
    const eyebrowInTopRow = isDark && !!eyebrow;
    const cardClass = [
        styles.card,
        checked ? styles.cardSelected : "",
        isDark ? styles.cardDark : "",
        className ?? "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <button
            type="button"
            className={cardClass}
            onClick={onSelect}
            aria-pressed={checked}
        >
            <div className={styles.inner}>
                {eyebrowInTopRow ? (
                    <div
                        className={`${styles.topRow} ${styles.topRowDark} ${styles.topRowWithEyebrow}`}
                    >
                        <div className={styles.eyebrowDark}>{eyebrow}</div>
                        <BookingFlowRadioIndicator
                            checked={checked}
                            inverse={isDark}
                        />
                    </div>
                ) : (
                    <>
                        <div className={styles.topRow}>
                            {icon ? (
                                <div className={styles.iconSlot}>{icon}</div>
                            ) : (
                                <span />
                            )}
                            <BookingFlowRadioIndicator
                                checked={checked}
                                inverse={isDark}
                            />
                        </div>
                        {eyebrow ? (
                            <div className={styles.eyebrow}>{eyebrow}</div>
                        ) : null}
                    </>
                )}
                <div className={styles.headlineRow}>
                    <div className={styles.headline}>{headline}</div>
                    {priceChip ? (
                        <span
                            className={`${styles.priceChip} ${
                                isDark ? styles.priceChipDark : ""
                            }`}
                        >
                            {priceChip}
                        </span>
                    ) : null}
                </div>
                {children}
            </div>
        </button>
    );
};

export const BookingFlowCardStack: React.FunctionComponent<{
    children: React.ReactNode;
}> = ({ children }) => <div className={styles.stack}>{children}</div>;

export const BookingFlowCardGrid: React.FunctionComponent<{
    children: React.ReactNode;
}> = ({ children }) => <div className={styles.cardGrid}>{children}</div>;

export { styles as bookingFlowSelectableCardStyles };
